<!--
  Client Portal v2: Approvals (PR D6).

  Rebuilt to the v2 design (planning .../handoff_2026-06-24/
  2026-06-24-client-portal-v2/design/sections-more.jsx -- the Approvals
  function + ProofList component + proof-review panel):

    - LIST view:
        * SectionHeader ("Approvals" + lede)
        * brand pending banner ("You have N proof(s) awaiting...") when >0
        * "Awaiting you" section (pending rows, code-avatar in brand color +
          Review button)
        * "History" section (decided rows, dark code-avatar + Approved /
          Changes-requested tag)
        * empty / loading / error states
    - PROOF-REVIEW detail (in-page view, not a route):
        * "Back to approvals" link
        * SectionHeader (eyebrow "Proof review")
        * proof image panel (uses proof_image_url when present, else the
          hatched placeholder + code badge)
        * "Decoration details" card
        * "Your decision" card: note textarea + Approve / Request-changes
          (Request-changes DISABLED until a note is entered)

  WORKING actions (not a stub): the review panel POSTs to the client-scoped
  approval endpoints, which reuse the canonical approval_service transitions:
    POST /api/portal/approvals/:id/approve
    POST /api/portal/approvals/:id/request-changes  { note }
  Both re-render the acted row into the History section from the returned
  record. Store Manager has the same approval access as Client Admin
  (approvals are not admin-gated).

  Wired to GET /api/portal/approvals (api/src/endpoints/portal.py), which
  scopes strictly to the caller's Client (403 on a non-client caller).
-->
<template>
  <v-container fluid class="portal-approvals" data-testid="portal-page-approvals">
    <!-- Loading -->
    <template v-if="loading">
      <v-progress-linear
        indeterminate
        color="primary"
        data-testid="approvals-loading"
      />
    </template>

    <!-- Error -->
    <template v-else-if="errorMsg">
      <h1 class="text-h5 mb-4">Approvals</h1>
      <v-alert
        type="error"
        variant="tonal"
        data-testid="approvals-error"
      >
        {{ errorMsg }}
        <div class="mt-2">
          <PortalButton
            variant="ghost"
            size="sm"
            data-testid="approvals-retry"
            @click="fetchApprovals"
          >
            <template #icon><v-icon size="16">refresh</v-icon></template>
            Retry
          </PortalButton>
        </div>
      </v-alert>
    </template>

    <!-- ===================== PROOF-REVIEW DETAIL ===================== -->
    <template v-else-if="reviewItem">
      <button
        type="button"
        class="approvals-back"
        data-testid="approvals-back"
        @click="closeReview"
      >
        <v-icon size="18">arrow_back</v-icon>
        Back to approvals
      </button>

      <div class="approvals-header" data-testid="approvals-review-header">
        <div class="approvals-eyebrow">Proof review</div>
        <h1 class="text-h5 mb-1">{{ reviewItem.name }}</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ methodStoreLine(reviewItem) }}. Review the artwork below, then
          approve it or request changes. Approval is required before it goes on
          products.
        </p>
      </div>

      <div class="approvals-review-grid">
        <!-- Proof image panel -->
        <v-card class="approvals-proof-card" data-testid="approvals-proof-image">
          <DecorationArtThumb
            v-if="reviewItem.proof_image_url"
            :src="reviewItem.proof_image_url"
            :ink-hex="reviewItem.proof_ink_hex || null"
            :ink-mixed="reviewItem.proof_ink_mixed === true"
            unknown-backdrop="light"
            :alt="`Proof for ${reviewItem.name}`"
            class="approvals-proof-img"
            data-testid="approvals-proof-img"
          />
          <div v-else class="approvals-proof-placeholder">
            <div class="approvals-proof-code">{{ reviewItem.code }}</div>
            <span class="approvals-proof-caption">
              decoration proof{{
                reviewItem.method ? ` · ${firstMethodWord(reviewItem)}` : ""
              }}
            </span>
          </div>
        </v-card>

        <!-- Right column: details + decision -->
        <div>
          <v-card class="mb-4" data-testid="approvals-detail-card">
            <div class="approvals-card-title">Decoration details</div>
            <div class="approvals-detail-body">
              <div
                v-for="(pair, i) in detailRows(reviewItem)"
                :key="i"
                class="approvals-detail-row"
                :class="{ 'approvals-detail-row--first': i === 0 }"
              >
                <span class="text-medium-emphasis">{{ pair[0] }}</span>
                <span class="font-weight-medium text-right">{{ pair[1] }}</span>
              </div>
            </div>
          </v-card>

          <!-- PENDING: the actionable decision card. -->
          <v-card
            v-if="reviewItem.state === 'pending'"
            data-testid="approvals-decision-card"
          >
            <div class="approvals-card-title">Your decision</div>
            <div class="approvals-detail-body">
              <v-textarea
                v-model="reviewNote"
                rows="3"
                variant="outlined"
                hide-details
                placeholder="If requesting changes, tell your distributor what to fix..."
                class="mb-3"
                data-testid="approvals-note"
              />
              <v-alert
                v-if="actionError"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-3"
                data-testid="approvals-action-error"
              >
                {{ actionError }}
              </v-alert>
              <div class="approvals-decision-actions">
                <PortalButton
                  variant="primary"
                  :loading="submitting === 'approve'"
                  :disabled="submitting !== null"
                  data-testid="approvals-approve"
                  @click="approve(reviewItem)"
                >
                  <template #icon><v-icon size="18">check</v-icon></template>
                  Approve proof
                </PortalButton>
                <PortalButton
                  variant="ghost"
                  :loading="submitting === 'changes'"
                  :disabled="!reviewNote.trim() || submitting !== null"
                  data-testid="approvals-request-changes"
                  @click="requestChanges(reviewItem)"
                >
                  <template #icon><v-icon size="18">edit_note</v-icon></template>
                  Request changes
                </PortalButton>
              </div>
            </div>
          </v-card>

          <!-- REPLACED: the decoration moved to a digitized version and the
               client holds a newer request for it (2026-10-09). This record
               stays as history; there is nothing to decide here. -->
          <v-card
            v-else-if="reviewItem.state === 'replaced'"
            class="mb-4"
            data-testid="approvals-replaced-card"
          >
            <div class="approvals-detail-body">
              <p class="mb-0">
                This proof was replaced by an updated one. Open the newer
                request in your approvals list.
              </p>
            </div>
          </v-card>

          <!-- NOT CURRENT: the decoration moved, but no newer request was
               sent to this client -- so point at nothing. -->
          <v-card
            v-else-if="reviewItem.state === 'not_current'"
            class="mb-4"
            data-testid="approvals-not-current-card"
          >
            <div class="approvals-detail-body">
              <p class="mb-0">This proof is no longer current.</p>
            </div>
          </v-card>

          <!-- DECIDED: read-only outcome. For a changes_requested proof, show
               the note the client submitted + the action history so it can be
               re-opened and read (D6-polish #2). Approved decided rows show a
               simple confirmation. No re-decision beyond the state machine. -->
          <template v-else>
            <v-card
              v-if="reviewItem.state === 'changes' && reviewItem.note"
              class="mb-4"
              data-testid="approvals-change-note-card"
            >
              <div class="approvals-card-title">Changes you requested</div>
              <div class="approvals-detail-body">
                <p
                  class="approvals-change-note"
                  data-testid="approvals-change-note"
                >
                  {{ reviewItem.note }}
                </p>
              </div>
            </v-card>

            <v-card
              v-if="reviewHistory.length"
              data-testid="approvals-history-card"
            >
              <div class="approvals-card-title">History</div>
              <div class="approvals-detail-body">
                <ul class="approvals-history" data-testid="approvals-history">
                  <li
                    v-for="(entry, i) in reviewHistory"
                    :key="i"
                    class="approvals-history__row"
                  >
                    <div class="approvals-history__meta">
                      <span class="approvals-history__author">{{
                        authorLabel(entry.author_role)
                      }}</span>
                      <span
                        v-if="entry.created_at"
                        class="approvals-history__when text-medium-emphasis"
                      >{{ historyWhen(entry.created_at) }}</span>
                    </div>
                    <div class="approvals-history__body">{{ entry.body }}</div>
                  </li>
                </ul>
              </div>
            </v-card>
          </template>
        </div>
      </div>
    </template>

    <!-- ===================== LIST VIEW ===================== -->
    <template v-else>
      <div class="approvals-header">
        <div class="approvals-eyebrow">Your stores</div>
        <h1 class="text-h5 mb-1">Approvals</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Artwork your distributor prepared for your stores. Review and approve
          proofs, or open one to request changes.
        </p>
      </div>

      <!-- Pending banner -->
      <v-alert
        v-if="pending.length"
        type="info"
        variant="tonal"
        class="mb-4 approvals-banner"
        data-testid="approvals-pending-banner"
      >
        <div class="approvals-banner__body">
          <span>
            You have
            <strong>{{ pending.length }} proof{{ pending.length > 1 ? "s" : "" }}</strong>
            awaiting your approval.
            {{ pending.length > 1 ? "They need" : "It needs" }} your sign-off
            before going on products.
          </span>
          <PortalButton
            variant="primary"
            size="sm"
            data-testid="approvals-review-now"
            @click="openReview(pending[0])"
          >
            Review now
          </PortalButton>
        </div>
      </v-alert>

      <!-- Empty state (no records at all) -->
      <div
        v-if="!items.length"
        class="approvals-empty"
        data-testid="approvals-empty"
      >
        <div class="approvals-empty__icon">
          <v-icon size="28" color="primary">task_alt</v-icon>
        </div>
        <h3 class="approvals-empty__title">Nothing waiting on you</h3>
        <p class="approvals-empty__body">
          When your distributor prepares decoration artwork for your stores,
          proofs land here for your sign-off. You're all caught up.
        </p>
      </div>

      <template v-else>
        <!-- Awaiting you -->
        <section
          v-if="pending.length"
          class="approvals-section"
          data-testid="approvals-section-awaiting"
        >
          <h3 class="approvals-section__title">Awaiting you</h3>
          <div class="approvals-list">
            <div
              v-for="item in pending"
              :key="item.id"
              class="approvals-row"
              :data-testid="`approvals-row-${item.id}`"
            >
              <div class="approvals-avatar approvals-avatar--pending">
                {{ item.code }}
              </div>
              <div class="approvals-row__main">
                <div class="approvals-row__name">{{ item.name }}</div>
                <div class="approvals-row__sub">{{ methodStoreLine(item) }}</div>
              </div>
              <span class="approvals-row__when text-medium-emphasis">
                {{ whenLabel(item) }}
              </span>
              <PortalButton
                variant="primary"
                size="sm"
                :data-testid="`approvals-review-${item.id}`"
                @click="openReview(item)"
              >
                <template #icon><v-icon size="16">rate_review</v-icon></template>
                Review
              </PortalButton>
            </div>
          </div>
        </section>

        <!-- History -->
        <section
          v-if="decided.length"
          class="approvals-section"
          data-testid="approvals-section-history"
        >
          <h3 class="approvals-section__title">History</h3>
          <div class="approvals-list">
            <div
              v-for="item in decided"
              :key="item.id"
              class="approvals-row"
              :data-testid="`approvals-row-${item.id}`"
            >
              <div class="approvals-avatar approvals-avatar--decided">
                {{ item.code }}
              </div>
              <div class="approvals-row__main">
                <div class="approvals-row__name">{{ item.name }}</div>
                <div class="approvals-row__sub">{{ methodStoreLine(item) }}</div>
              </div>
              <span class="approvals-row__when text-medium-emphasis">
                {{ whenLabel(item) }}
              </span>
              <span
                v-if="item.state === 'changes'"
                class="approvals-tag approvals-tag--warning"
                :data-testid="`approvals-status-${item.id}`"
              >
                <v-icon size="12">edit_note</v-icon>
                Changes requested
              </span>
              <span
                v-else-if="item.state === 'replaced'"
                class="approvals-tag approvals-tag--muted"
                :data-testid="`approvals-status-${item.id}`"
              >
                <v-icon size="12">history</v-icon>
                Replaced
              </span>
              <span
                v-else-if="item.state === 'not_current'"
                class="approvals-tag approvals-tag--muted"
                :data-testid="`approvals-status-${item.id}`"
              >
                <v-icon size="12">history</v-icon>
                Not current
              </span>
              <span
                v-else
                class="approvals-tag approvals-tag--success"
                :data-testid="`approvals-status-${item.id}`"
              >
                <v-icon size="12">check_circle</v-icon>
                Approved
              </span>
              <!-- Re-open a decided proof read-only (see the requested-changes
                   note + history). D6-polish #2. -->
              <PortalButton
                variant="ghost"
                size="sm"
                :data-testid="`approvals-open-${item.id}`"
                @click="openReview(item)"
              >
                <template #icon><v-icon size="16">visibility</v-icon></template>
                Open
              </PortalButton>
            </div>
          </div>
        </section>
      </template>
    </template>
  </v-container>
</template>

<script>
import axios from "@/axios";
import { usePortalScopeStore } from "@/stores/portalScope";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";
import PortalButton from "@/components/portal/PortalButton.vue";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";

export default {
  name: "PortalApprovals",
  components: { DecorationArtThumb, PortalButton },
  setup() {
    // The GLOBAL header store scope (ADR 0055). Approvals passes the selected
    // store_id so the endpoint validates it against the caller's client (a
    // cross-client store_id 404s). Decoration approvals have no store
    // dimension yet (the artifact->store linkage is deferred, GH #1306), so a
    // store scope does not narrow this list -- but the request stays
    // consistent with the other scoped surfaces and re-validates on change.
    const scope = usePortalScopeStore();
    // After an approve / request-changes we emit sidebar:refresh('approvals')
    // so the left-nav "N awaiting . M in changes" sub-text updates live
    // without a page reload. The portalNavSummary store subscribes and owns
    // the refetch (generic delegated mechanism, PR-E).
    return { scope };
  },
  data() {
    return {
      items: [],
      loading: true,
      errorMsg: "",
      // Monotonic fetch counter for the store-scope re-fetch race guard.
      _reqSeq: 0,
      // Proof-review detail state.
      reviewId: null,
      reviewNote: "",
      // submitting: null | 'approve' | 'changes'
      submitting: null,
      actionError: "",
    };
  },
  computed: {
    pending() {
      return this.items.filter((i) => i.state === "pending");
    },
    decided() {
      return this.items.filter((i) => i.state !== "pending");
    },
    reviewItem() {
      if (this.reviewId === null) return null;
      return this.items.find((i) => i.id === this.reviewId) || null;
    },
    // The action thread for the item under review (D6-polish #2). Empty for a
    // pending item or one the server returned without history.
    reviewHistory() {
      const h = this.reviewItem && this.reviewItem.history;
      return Array.isArray(h) ? h : [];
    },
  },
  created() {
    this.fetchApprovals();
  },
  watch: {
    // Re-fetch when the global header store scope changes so the request
    // re-validates against the selected store (list stays client-scoped).
    "scope.selectedStoreId"() {
      this.fetchApprovals();
    },
  },
  methods: {
    async fetchApprovals() {
      // Request-sequence guard against out-of-order responses when the store
      // scope changes (or an approve/request-changes 409 triggers a recovery
      // re-fetch concurrently). See Orders.vue for the rationale.
      const seq = ++this._reqSeq;
      this.loading = true;
      this.errorMsg = "";
      try {
        const resp = await axios.get("/api/portal/approvals", {
          params: this.scope.storeIdParams,
        });
        if (seq !== this._reqSeq) return;
        this.items = resp?.data?.approvals ?? [];
        this.openRoutedReview();
      } catch (e) {
        if (seq !== this._reqSeq) return;
        this.errorMsg = "Could not load your approvals.";
      } finally {
        if (seq === this._reqSeq) this.loading = false;
      }
    },
    openReview(item) {
      this.reviewId = item.id;
      this.reviewNote = "";
      this.actionError = "";
    },
    /**
     * Open the proof named by `/portal/approvals/:id` (GH #3088).
     *
     * This route is what the approval email links to, replacing the
     * `/approve/<token>` magic link. Landing the client on the LIST and
     * making them hunt for the row the email was about would lose most
     * of the value of a direct link -- and with a design fanned out
     * across several sizes, several rows now look nearly identical.
     *
     * A routed id the client cannot see (the backend scopes the list to
     * their Client and 404s a foreign record) simply falls through to
     * the list rather than stranding them on an empty review panel.
     * `reviewItem` already resolves to null for an unknown id; this
     * leaves reviewId null so the list renders.
     */
    openRoutedReview() {
      const routed = this.$route?.params?.id;
      if (routed === undefined || routed === null || routed === "") return;
      const wanted = Number(routed);
      if (!Number.isFinite(wanted)) return;
      const match = this.items.find((i) => i.id === wanted);
      if (match) this.openReview(match);
    },
    closeReview() {
      this.reviewId = null;
      this.reviewNote = "";
      this.actionError = "";
    },
    methodStoreLine(item) {
      const parts = [item.method, item.store].filter(Boolean);
      return parts.length ? parts.join(" · ") : "Decoration proof";
    },
    firstMethodWord(item) {
      if (!item.method) return "";
      return item.method.split(" · ")[0];
    },
    detailRows(item) {
      return [
        ["Method", item.method || "--"],
        ["Store", item.store || "--"],
        ["Status", this.statusLabel(item)],
        ["Sent", this.whenLabel(item) || "--"],
      ];
    },
    statusLabel(item) {
      if (item.state === "approved") return "Approved";
      if (item.state === "changes") return "Changes requested";
      if (item.state === "replaced") return "Replaced";
      if (item.state === "not_current") return "Not current";
      return "Awaiting your approval";
    },
    whenLabel(item) {
      // A superseded proof may never have been decided; its date is when it
      // was sent.
      const iso = ["pending", "replaced", "not_current"].includes(item.state)
        ? item.requested_at
        : item.decided_at;
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return "";
      }
    },
    // Human speaker label for a history entry's author_role (D6-polish #2).
    // The client sees "You" for their own change requests and friendly labels
    // for the distributor side / system narration.
    authorLabel(role) {
      const map = {
        owner: "You",
        builder: "Your distributor",
        digitizer: "Digitizer",
        system: "System",
      };
      return map[role] || "Update";
    },
    historyWhen(iso) {
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch (e) {
        return "";
      }
    },
    // Replace an item in place with the server's updated record so the row
    // re-renders into the History section from the returned state.
    _applyUpdated(updated) {
      if (!updated || updated.id == null) return;
      const idx = this.items.findIndex((i) => i.id === updated.id);
      if (idx !== -1) {
        this.items.splice(idx, 1, updated);
      }
    },
    // A 409 means the proof was already decided (by another tab / the
    // distributor) while this view was stale. Re-fetch so the row moves to
    // History and the client sees the current state, rather than retrying
    // into the same 409 forever.
    _isStaleConflict(e) {
      return e?.response?.status === 409;
    },
    async approve(item) {
      if (this.submitting) return;
      this.submitting = "approve";
      this.actionError = "";
      try {
        const resp = await axios.post(
          `/api/portal/approvals/${item.id}/approve`
        );
        this._applyUpdated(resp?.data?.approval);
        this.closeReview();
        // Live-refresh the sidebar sub-text (approving removes an
        // awaiting-you item). Generic delegated mechanism (PR-E):
        // the portalNavSummary store subscribes and refetches.
        emitSidebarRefresh("approvals");
      } catch (e) {
        if (this._isStaleConflict(e)) {
          this.closeReview();
          await this.fetchApprovals();
          this.errorMsg = "";
        } else {
          this.actionError = "Could not approve this proof. Please try again.";
        }
      } finally {
        this.submitting = null;
      }
    },
    async requestChanges(item) {
      if (this.submitting) return;
      const note = this.reviewNote.trim();
      if (!note) return;
      this.submitting = "changes";
      this.actionError = "";
      try {
        const resp = await axios.post(
          `/api/portal/approvals/${item.id}/request-changes`,
          { note }
        );
        this._applyUpdated(resp?.data?.approval);
        this.closeReview();
        // Live-refresh the sidebar sub-text (an item moved awaiting -> in
        // changes). Generic delegated mechanism (PR-E): the portalNavSummary
        // store subscribes and refetches.
        emitSidebarRefresh("approvals");
      } catch (e) {
        if (this._isStaleConflict(e)) {
          this.closeReview();
          await this.fetchApprovals();
          this.errorMsg = "";
        } else {
          this.actionError =
            "Could not send your change request. Please try again.";
        }
      } finally {
        this.submitting = null;
      }
    },
  },
};
</script>

<style scoped>
.approvals-header {
  margin-bottom: 22px;
}
.approvals-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--tf-brand-ink, #1565c0);
  margin-bottom: 6px;
}
.approvals-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--tf-brand-ink, #1565c0);
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 16px;
  padding: 4px;
}
.approvals-banner__body {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: space-between;
}

/* ---- List rows ---- */
.approvals-section {
  margin-bottom: 8px;
}
.approvals-section__title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
  margin: 18px 0 10px;
}
.approvals-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.approvals-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  background: var(--tf-surface, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 12px);
}
.approvals-avatar {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.approvals-avatar--pending {
  background: var(--tf-brand, #1565c0);
  color: var(--tf-on-brand, #fff);
}
.approvals-avatar--decided {
  background: #33445a;
  color: #fff;
}
.approvals-row__main {
  flex: 1;
  min-width: 0;
}
.approvals-row__name {
  font-size: 14px;
  color: var(--tf-fg-1, #212121);
  font-weight: 500;
}
.approvals-row__sub {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.approvals-row__when {
  font-size: 11px;
  white-space: nowrap;
}
.approvals-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: var(--tf-radius-pill, 9999px);
  white-space: nowrap;
}
.approvals-tag--success {
  color: var(--tf-success-dark, #2e7d32);
  background: var(--tf-success-soft, #e6f7ed);
}
.approvals-tag--muted {
  color: var(--tf-text-muted, #5f6368);
  background: var(--tf-surface-muted, #f1f3f4);
}
.approvals-tag--warning {
  color: var(--tf-warning-dark, #8a6d3b);
  background: var(--tf-warning-soft, #fff8e1);
}

/* ---- Empty ---- */
.approvals-empty {
  text-align: center;
  padding: 52px 28px;
  border: 1px dashed var(--tf-border-strong, #d6d6d6);
  border-radius: var(--tf-radius-md, 12px);
  background: var(--tf-surface-alt, #fafbfc);
}
.approvals-empty__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--tf-brand-soft, #e3f2fd);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.approvals-empty__title {
  font-size: 18px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
  margin: 0 0 6px;
}
.approvals-empty__body {
  font-size: 14px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.55;
  max-width: 420px;
  margin: 0 auto;
}

/* ---- Review detail ---- */
.approvals-review-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
}
.approvals-proof-card {
  overflow: hidden;
}
.approvals-proof-img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: contain;
  background: #eef1f4;
}
.approvals-proof-placeholder {
  position: relative;
  aspect-ratio: 4 / 3;
  background: repeating-linear-gradient(
    45deg,
    #eef1f4,
    #eef1f4 12px,
    #e7ebef 12px,
    #e7ebef 24px
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
.approvals-proof-code {
  width: 96px;
  height: 96px;
  border-radius: 12px;
  background: var(--tf-brand, #1565c0);
  color: var(--tf-on-brand, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 34px;
}
.approvals-proof-caption {
  font-family: var(--tf-font-mono, monospace);
  font-size: 11px;
  color: var(--tf-fg-4, #757575);
  background: #fff;
  padding: 3px 8px;
  border-radius: 4px;
}
.approvals-card-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
  padding: 16px 20px 14px;
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.approvals-detail-body {
  padding: 18px 20px;
}
.approvals-detail-row {
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-top: 1px solid var(--tf-border, #e0e0e0);
  font-size: 13px;
}
.approvals-detail-row--first {
  border-top: none;
}
.approvals-decision-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ---- Read-only decided detail: requested-changes note + history ---- */
.approvals-change-note {
  margin: 0;
  font-size: 14px;
  color: var(--tf-fg-1, #212121);
  line-height: 1.55;
  white-space: pre-wrap;
}
.approvals-history {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.approvals-history__row {
  padding: 10px 12px;
  background: var(--tf-surface-alt, #fafbfc);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 12px);
}
.approvals-history__meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.approvals-history__author {
  font-size: 12px;
  font-weight: 600;
  color: var(--tf-fg-2, #424242);
}
.approvals-history__when {
  font-size: 11px;
  white-space: nowrap;
}
.approvals-history__body {
  font-size: 13px;
  color: var(--tf-fg-2, #424242);
  line-height: 1.5;
  white-space: pre-wrap;
}

@media (max-width: 700px) {
  .approvals-review-grid {
    grid-template-columns: 1fr;
  }
}
</style>
